import { JobMode, JobStatus } from '@flatfile/api-internal/api'
import {
  Badge,
  Icon,
  IconName,
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Progress,
  Text,
  cn,
} from '@flatfile/baseline'
import { useState } from 'react'
import { ResourceList } from '../../components/ResourceList/ResourceList'
import { ResourceListContextProvider } from '../../components/ResourceList/ResourceListContext'
import { ListPagination, ResourceRowComponent } from '../../components/ResourceList/types'
import { CustomCopyButton } from '../../components/Shared/CopyToClipboard'
import { NoAccessError } from '../../components/Shared/CustomErrorComponent'

import TrialBanner from '../../components/Shared/TrialBanner'
import { useDynamicHeight } from '../../hooks/useDynamicHeight'
import { useActiveEnvironment } from '../../hooks/useEnvironments'
import { useIdentity } from '../../hooks/useIdentity'
import useResource from '../../resources/hooks/useResource'
import { formatDate, formatTime, getFormattedDuration } from '../../utils/format'

const ICON_SIZE = 12

const IconPill = ({ icon, text }: { icon: IconName; text?: string }) => (
  <div className='flex items-center gap-1.5 flex-nowrap'>
    <Icon name={icon} size={ICON_SIZE} />
    <Text className='text-sm font-normal'>{text ?? 'Unknown'}</Text>
  </div>
)

const getIconForOperation = (operation: string): IconName => {
  switch (operation) {
    case 'configure':
      return 'toolbox'
    case 'extract':
      return 'arrow-out-of-box'
    case 'map':
      return 'bezier-curve'
    default:
      return 'truck'
  }
}

const getHumanReadableJobMode = (mode?: JobMode): string => {
  switch (mode) {
    case JobMode.Background:
      return 'background'
    case JobMode.Foreground:
      return 'foreground'
    case JobMode.ToolbarBlocking:
      return 'toolbar-blocking'
    default:
      return 'unknown'
  }
}

const getColorForProgressLevelAndStatus = (progressLevel?: number, status?: JobStatus): string => {
  if (status === JobStatus.Failed) return 'destructive'
  if (progressLevel === undefined || progressLevel === null) return 'primary'
  if (progressLevel < 100) return 'primary'
  return 'success'
}

const JobsHeader = () => (
  <thead className='border-b border-gray-200'>
    <tr>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Operation
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Job ID
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Space ID
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Progress
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Timestamp
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          Duration
        </Text>
      </th>
      <th className='px-3 py-2 text-left align-middle min-w-[80px] h-[42px]'>
        <Text variant='subheadline' emphasized className='text-muted'>
          # Fields
        </Text>
      </th>
    </tr>
  </thead>
)

const JobRow: ResourceRowComponent = ({ resourceId }) => {
  const job = useResource('JOBS', resourceId)
  if (!job) {
    return <></>
  }

  return (
    <tr className='hover:bg-gray-50 rounded-md overflow-hidden p-1'>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ width: '27%' }}>
        <IconPill icon={getIconForOperation(job?.operation)} text={job.operation} />
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ width: '15%' }}>
        <CustomCopyButton value={job.id} />
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ width: '15%' }}>
        <CustomCopyButton value={job.spaceId || 'N/A'} />
      </td>
      <td
        className='px-3 py-2 text-left align-middle border-b border-gray-200'
        style={{ minWidth: '200px', width: '27%' }}
      >
        <div className='space-y-1'>
          <Progress
            value={job.progress ?? 0}
            max={100}
            className={cn('border', job.status === JobStatus.Executing ? 'animate-pulse' : '')}
            color={getColorForProgressLevelAndStatus(job.progress, job.status)}
          />
          <div className='flex justify-between mt-1 items-baseline gap-2'>
            <Text className='text-xs'>{job.progress}%</Text>
            <div className='inline-flex items-center gap-2'>
              <Badge variant='secondary' className='text-xxs p-1'>
                {getHumanReadableJobMode(job.mode)}
              </Badge>{' '}
              <div className='text-xxs text-muted'>{job.status}</div>
            </div>
          </div>
        </div>
      </td>

      <td
        className='px-3 py-2 text-left align-middle border-b border-gray-200'
        style={{ minWidth: '150px', width: '21%' }}
      >
        {!job.startedAt ? (
          <div className='flex items-center gap-1.5'>
            <Text className='text-sm font-normal'>
              <Text className='text-xs text-muted'>Created</Text> {formatDate(job.createdAt)}{' '}
              {formatTime(job.createdAt, true)}
            </Text>
          </div>
        ) : (
          <div className='flex items-center gap-1.5'>
            <Text className='text-sm font-normal'>
              <Text className='text-xs text-muted'>Started</Text> {formatDate(job.startedAt)}{' '}
              {formatTime(job.startedAt, true)}
            </Text>
          </div>
        )}
      </td>

      <td className='px-3 py-2 text-left align-middle border-b border-gray-200' style={{ width: '80px' }}>
        {!job.startedAt ? (
          <div className='flex items-center'>
            <Text className='text-sm text-muted'>-</Text>
          </div>
        ) : (
          <div className='flex items-center'>
            <Text className='text-sm font-normal'>{getFormattedDuration(job.startedAt, job.finishedAt)}</Text>
          </div>
        )}
      </td>
      <td className='px-3 py-2 text-left align-middle border-b border-gray-200 min-w-[80px]' style={{ width: '80px' }}>
        {job.input?.length > 1 ? (
          <div className='flex items-center'>
            <Text className='text-sm font-normal'>{job.input?.length}</Text>
          </div>
        ) : (
          <div className='flex items-center'>
            <Text className='text-sm text-muted'>-</Text>
          </div>
        )}
      </td>
    </tr>
  )
}

export const JobsPage = () => {
  const [currentPage, setCurrentPage] = useState(1)
  const [pageCount, setPageCount] = useState(0)
  const pagination: ListPagination = { currentPage, setCurrentPage, pageCount, setPageCount }

  const { addToRefsArray } = useDynamicHeight()
  const { hasPermission } = useIdentity()
  const { environment } = useActiveEnvironment()
  const canSeeJobs = hasPermission({ resource: environment, permissionKey: 'environment:read' })

  return canSeeJobs ? (
    <div ref={addToRefsArray} className='w-full h-full flex flex-col p-3'>
      <div className='p-3 flex justify-between'>
        <Text variant='title2'>All Jobs</Text>
        <TrialBanner />
      </div>
      <div>
        <div ref={addToRefsArray}>
          <div className='flex justify-between items-center px-2 w-full py-4'>
            <div className='flex-1'>{/* Search here eventually */}</div>
            <Pagination className='py-2 justify-end' iconOnly size='sm'>
              <PaginationContent>
                <PaginationPrevious
                  onClick={() => {
                    if (currentPage > 1) setCurrentPage(currentPage - 1)
                  }}
                  disabled={currentPage <= 1}
                />
                {(() => {
                  const maxVisiblePages = 5
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
                  // Always show at least one page
                  const effectivePageCount = Math.max(1, pagination.pageCount)
                  const endPage = Math.min(effectivePageCount, startPage + maxVisiblePages - 1)

                  // Adjust start page if we're near the end
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1)
                  }

                  // Generate array of page numbers to display
                  const pages = Array.from({ length: endPage - startPage + 1 }, (_, i) => startPage + i)

                  return pages.map((pageNum) => (
                    <PaginationItem key={pageNum}>
                      <PaginationLink
                        isActive={pageNum === currentPage}
                        onClick={() => setCurrentPage(pageNum)}
                        className={cn(
                          'w-5 h-5 flex items-center justify-center rounded-full p-0',
                          pageNum === currentPage
                            ? 'bg-surface-sentiment-subtle-neutral border-none'
                            : 'hover:bg-gray-100'
                        )}
                      >
                        {pageNum}
                      </PaginationLink>
                    </PaginationItem>
                  ))
                })()}
                <PaginationNext
                  onClick={() => {
                    if (currentPage < pagination.pageCount) setCurrentPage(currentPage + 1)
                  }}
                  disabled={currentPage >= pagination.pageCount || pagination.pageCount <= 1}
                />
              </PaginationContent>
            </Pagination>
          </div>
        </div>
      </div>
      <div className='relative'>
        <div className='overflow-x-auto h-full relative'>
          <ResourceListContextProvider requestParams={{ sortDirection: 'desc' }} resourceType='JOBS'>
            <ResourceList
              pagination={pagination}
              headerComponent={JobsHeader}
              rowComponent={JobRow}
              errorMessage='Error loading jobs.'
              emptyMessage='No jobs to display in this environment.'
            />
          </ResourceListContextProvider>
        </div>
      </div>
    </div>
  ) : (
    <NoAccessError page='jobs.page.tsx'></NoAccessError>
  )
}
